import { useEffect, useState } from 'react';
import { useKlantenStore, type ContactCreateFields, type ContactInfo } from '../../stores/klanten-store';

interface Props {
  isOpen: boolean;
  contact: ContactInfo | null;
  accountName: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  mobilePhone: string;
  title: string;
  department: string;
}

const EMPTY: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  mobilePhone: '',
  title: '',
  department: '',
};

/**
 * Bewerk een bestaand Contact. Pre-fillt vanuit `contact`. Submit alleen
 * gewijzigde velden als patch (server kent NO_CHANGES — wij doen client-side
 * diff zodat onnodige round-trips weg blijven).
 *
 * ContactInfo splitst `name` in een single string — we proberen first/last te
 * raden via split-op-eerste-spatie. Bij submit sturen we de twee velden los
 * mee zodat de server precies kan opslaan wat gebruiker bedoelt.
 */
export function EditContactDialog({ isOpen, contact, accountName, onClose, onSuccess }: Props) {
  const updateContact = useKlantenStore((s) => s.updateContact);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [original, setOriginal] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !contact) {
      setForm(EMPTY);
      setOriginal(EMPTY);
      setError(null);
      setSubmitting(false);
      return;
    }
    // Best-effort split — name kan "Pieter Jansen" of "Pieter van der Berg" zijn
    const parts = contact.name.split(' ').filter(Boolean);
    const firstName = parts.length > 1 ? parts[0] : '';
    const lastName = parts.length > 1 ? parts.slice(1).join(' ') : contact.name;
    const initial: FormState = {
      firstName,
      lastName,
      email: contact.email || '',
      phone: contact.phone || '',
      mobilePhone: '', // ContactInfo bevat geen aparte mobile — leeg tonen
      title: contact.title || '',
      department: contact.department || '',
    };
    setForm(initial);
    setOriginal(initial);
  }, [isOpen, contact]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, submitting, onClose]);

  if (!isOpen || !contact) return null;

  const trimmedLastName = form.lastName.trim();
  const canSubmit = trimmedLastName.length > 0 && !submitting;

  function buildPatch(): Partial<ContactCreateFields> {
    const patch: Partial<ContactCreateFields> = {};
    const fields: Array<[keyof FormState, keyof ContactCreateFields]> = [
      ['firstName', 'FirstName'],
      ['lastName', 'LastName'],
      ['email', 'Email'],
      ['phone', 'Phone'],
      ['mobilePhone', 'MobilePhone'],
      ['title', 'Title'],
      ['department', 'Department'],
    ];
    for (const [key, sfKey] of fields) {
      const next = form[key].trim();
      const prev = original[key].trim();
      if (next !== prev) {
        // Lege string → SF mag null krijgen (clear field). Behalve LastName.
        (patch as Record<string, string>)[sfKey] = next;
      }
    }
    return patch;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    if (!contact) return;

    const patch = buildPatch();
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }

    setSubmitting(true);
    setError(null);

    const result = await updateContact(contact.id, patch);
    setSubmitting(false);

    if (!result.ok) {
      setError(result.error || 'Bijwerken mislukt');
      return;
    }
    onSuccess();
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40"
      onClick={() => { if (!submitting) onClose(); }}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-base font-semibold text-slate-900">Contactpersoon bewerken</h2>
            <p className="text-xs text-slate-500 mt-1">Bij {accountName}</p>
          </div>

          <div className="px-6 py-4 space-y-3">
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Voornaam</label>
                <input
                  type="text"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Achternaam <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  required
                  disabled={submitting}
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">E-mailadres</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                disabled={submitting}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Telefoon</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Mobiel</label>
                <input
                  type="tel"
                  value={form.mobilePhone}
                  onChange={(e) => setForm({ ...form, mobilePhone: e.target.value })}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Functie</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Afdeling</label>
                <input
                  type="text"
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
            </div>
          </div>

          <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-1.5 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              {submitting ? 'Opslaan…' : 'Opslaan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
